import { CollisionPriority } from '@dnd-kit/abstract';
import { directionBiased, pointerIntersection } from '@dnd-kit/collision';
import { useDroppable } from '@dnd-kit/react';

import { cn } from '@/lib/utils';
import type { DropParentDirection, GapZoneData } from '../core';

type GapZoneIntent = 'insert' | 'group';

export function getInsertEdgesForParentDirection(
  parentDirection: DropParentDirection,
): Array<GapZoneData['edge']> {
  if (parentDirection === 'grid') {
    return ['left', 'right'];
  }

  return parentDirection === 'horizontal' ? ['left', 'right'] : ['top', 'bottom'];
}

export function getGroupEdgesForParentDirection(
  parentDirection: DropParentDirection,
): Array<GapZoneData['edge']> {
  if (parentDirection === 'grid') {
    return [];
  }

  return parentDirection === 'vertical' ? ['left', 'right'] : ['top', 'bottom'];
}

interface GapZoneProps {
  blockId: string;
  parentId: string | null;
  parentDirection: DropParentDirection;
  disabled?: boolean;
}

interface EdgeDropTargetProps extends GapZoneProps {
  edge: GapZoneData['edge'];
  intent: GapZoneIntent;
}

const getEdgeZoneClassName = ({
  edge,
  intent,
  parentDirection,
}: {
  edge: GapZoneData['edge'];
  intent: GapZoneIntent;
  parentDirection: DropParentDirection;
}): string => {
  switch (edge) {
    case 'top':
      if (parentDirection === 'horizontal' && intent === 'group') {
        return 'left-0 right-0 -top-4 h-4';
      }
      return 'left-0 right-0 -top-2 h-5';
    case 'bottom':
      if (parentDirection === 'horizontal' && intent === 'group') {
        return 'left-0 right-0 -bottom-4 h-4';
      }
      return 'left-0 right-0 -bottom-2 h-5';
    case 'left':
      if (parentDirection === 'horizontal' && intent === 'insert') {
        return 'left-0 top-0 bottom-0 w-1/3';
      }
      return 'top-0 bottom-0 -left-4 w-8';
    case 'right':
      if (parentDirection === 'horizontal' && intent === 'insert') {
        return 'right-0 top-0 bottom-0 w-1/3';
      }
      return 'top-0 bottom-0 -right-4 w-8';
  }
};

const getGapZoneDroppableId = ({
  intent,
  edge,
  blockId,
}: {
  intent: GapZoneIntent;
  edge: GapZoneData['edge'];
  blockId: string;
}): string => `gap-${intent}-${edge}-${blockId}`;

const getGapZoneType = (intent: GapZoneIntent): GapZoneData['type'] =>
  intent === 'group' ? 'group-gap-zone' : 'insert-gap-zone';

function EdgeDropTarget({
  blockId,
  parentId,
  parentDirection,
  disabled,
  edge,
  intent,
}: EdgeDropTargetProps) {
  const { ref } = useDroppable({
    id: getGapZoneDroppableId({ intent, edge, blockId }),
    data: {
      type: getGapZoneType(intent),
      blockId,
      edge,
      parentId,
      parentDirection,
    } satisfies GapZoneData,
    disabled,
    collisionPriority: CollisionPriority.High,
    collisionDetector: parentDirection === 'horizontal' ? pointerIntersection : directionBiased,
  });

  return (
    <div
      ref={ref}
      className={cn('absolute z-20', getEdgeZoneClassName({ edge, intent, parentDirection }))}
    />
  );
}

export function GapZone({ blockId, parentId, parentDirection, disabled }: GapZoneProps) {
  const insertEdges = getInsertEdgesForParentDirection(parentDirection);
  const groupEdges = getGroupEdgesForParentDirection(parentDirection);

  return (
    <div className={cn('absolute inset-0 z-20', disabled && 'pointer-events-none')}>
      {insertEdges.map((edge) => (
        <EdgeDropTarget
          key={`insert-${edge}`}
          blockId={blockId}
          parentId={parentId}
          parentDirection={parentDirection}
          disabled={disabled}
          edge={edge}
          intent="insert"
        />
      ))}
      {groupEdges.map((edge) => (
        <EdgeDropTarget
          key={`group-${edge}`}
          blockId={blockId}
          parentId={parentId}
          parentDirection={parentDirection}
          disabled={disabled}
          edge={edge}
          intent="group"
        />
      ))}
    </div>
  );
}
